import type { PermitTypeContext } from "@offstreetparking/domain/rules";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { AccessibleContent } from "../../accessibility";
import { formatDate, formatDateRange } from "../../formatters";
import { Button } from "../../forms";
import { ArrowRightIcon } from "../../icons/ArrowRightIcon";
import { DateIcon } from "../../icons/DateIcon";
import { DateRangeIcon } from "../../icons/DateRangeIcon";
import { useDesignSystemI18n } from "../../providers/DesignSystemI18nContext";
import { useLocale } from "../../providers/DesignSystemLocaleContext";
import { Text } from "../../typography";
import { classNames } from "../../utils";
import { ParkingTimeSchedule } from "./ParkingTimeSchedule";
import { RulesPreview } from "./RulesPreview";
import { TimeBadge } from "./TimeBadge";
import { type ComparisonSide, changedValueClassName, valueIntent } from "./comparison";
import { type IntervalCollapse, PORTAL_INTERVAL_COLLAPSE, getIntervalCollapse } from "./interval-collapse";

export type PermitIntervalType = "SINGLE" | "DATE_RANGE";

export const PERMIT_INTERVAL_TYPE = {
	SINGLE: "SINGLE",
	DATE_RANGE: "DATE_RANGE",
} as const;

type PermitIntervals = {
	startDateTimeISO: string;
	endDateTimeISO?: string | null;
	schedules?: {
		startTime: string;
		endTime: string;
		day: "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
	}[];
};

export type ComparedOption = {
	timezone: string;
	permitIntervals?: PermitIntervals[];
	context?: PermitTypeContext | null;
};

export type OptionComparison = {
	side: ComparisonSide;
	other: ComparedOption;
};

type ParkingOptionProps = {
	timezone: string;
	permitIntervals?: PermitIntervals[];
	defaultShowAllIntervals?: boolean;
	collapse?: IntervalCollapse;
	isInstant: boolean;
	context?: PermitTypeContext | null;
	comparison?: OptionComparison;
};

export const ParkingOption = ({
	timezone,
	permitIntervals,
	defaultShowAllIntervals,
	collapse = PORTAL_INTERVAL_COLLAPSE,
	isInstant = false,
	context,
	comparison,
}: ParkingOptionProps) => {
	const { parkingOption } = useDesignSystemI18n();
	const { locale } = useLocale();
	const [showAllIntervals, setShowAllIntervals] = useState(defaultShowAllIntervals ?? false);

	const makeIntervals = useCallback(
		(permitIntervals: PermitIntervals[]) => {
			return (
				permitIntervals
					?.map((interval, index) => {
						const startDateTime = DateTime.fromISO(interval.startDateTimeISO).setZone(timezone);
						const endDateTime = interval.endDateTimeISO
							? DateTime.fromISO(interval.endDateTimeISO).setZone(timezone)
							: undefined;
						return {
							index,
							startDateTimeISO: interval.startDateTimeISO,
							endDateTimeISO: interval.endDateTimeISO ?? undefined,
							type:
								startDateTime.toFormat("yyyy-MM-dd") === endDateTime?.toFormat("yyyy-MM-dd")
									? PERMIT_INTERVAL_TYPE.SINGLE
									: PERMIT_INTERVAL_TYPE.DATE_RANGE,
							schedules: interval.schedules,
						};
					})
					?.reduce(
						(
							acc: {
								key: string;
								index: number;
								type: PermitIntervalType;
								startDateTimeISO: string;
								endDateTimeISO: string | undefined;
								times: { index: number; startTime: string; endTime: string | null | undefined }[];
								hasSchedule: boolean;
								schedules?: {
									startTime: string;
									endTime: string;
									day:
										| "MONDAY"
										| "TUESDAY"
										| "WEDNESDAY"
										| "THURSDAY"
										| "FRIDAY"
										| "SATURDAY"
										| "SUNDAY";
								}[];
							}[],
							curr,
						) => {
							const key = `${curr.startDateTimeISO}_${curr.endDateTimeISO}`;

							const currStartDate = DateTime.fromISO(curr.startDateTimeISO)
								.setZone(timezone)
								.toFormat("yyyy-MM-dd");
							const currEndDate = curr.endDateTimeISO
								? DateTime.fromISO(curr.endDateTimeISO).setZone(timezone).toFormat("yyyy-MM-dd")
								: undefined;

							const found = acc.find(
								(item) =>
									DateTime.fromISO(item.startDateTimeISO).setZone(timezone).toFormat("yyyy-MM-dd") ===
										currStartDate &&
									(item.endDateTimeISO
										? DateTime.fromISO(item.endDateTimeISO).setZone(timezone).toFormat("yyyy-MM-dd")
										: undefined) === currEndDate,
							);
							if (found) {
								found.times.push({
									index: curr.index,
									startTime: curr.startDateTimeISO,
									endTime: curr.endDateTimeISO,
								});
							} else {
								acc.push({
									key,
									index: curr.index,
									startDateTimeISO: curr.startDateTimeISO,
									endDateTimeISO: curr.endDateTimeISO,
									type: curr.type,
									times: [
										{
											index: curr.index,
											startTime: curr.startDateTimeISO,
											endTime: curr.endDateTimeISO,
										},
									],
									schedules: curr.schedules,
									hasSchedule: (curr.schedules ?? []).length > 0,
								});
							}
							return acc;
						},
						[],
					) ?? []
			);
		},
		[timezone],
	);

	const intervals = makeIntervals(permitIntervals ?? []);
	const otherIntervals = comparison?.other.permitIntervals ?? [];
	const otherTimezone = comparison?.other.timezone ?? timezone;
	const side = comparison?.side;
	const textIntent = valueIntent(side);

	const dateText = (dateTimeISO: string, zone = timezone) =>
		formatDate({ date: dateTimeISO, formatType: "Abbreviated_Date", timezone: zone, language: locale });
	const timeText = (dateTimeISO: string, zone = timezone) =>
		formatDate({ date: dateTimeISO, formatType: "Abbreviated_Time", timezone: zone, language: locale });
	const timeRangeText = (startDateTimeISO: string, endDateTimeISO: string | null | undefined, zone = timezone) =>
		formatDateRange({
			startDate: startDateTimeISO,
			endDate: endDateTimeISO!,
			formatType: "Abbreviated_Time",
			timezone: zone,
			language: locale,
		});
	const isChanged = (
		format: (dateTimeISO: string, zone?: string) => string,
		dateTimeISO: string | null | undefined,
		otherISO: string | null | undefined,
	) => !!comparison && (dateTimeISO && format(dateTimeISO)) !== (otherISO && format(otherISO, otherTimezone));
	const isTimeRangeChanged = (
		time: { startTime: string; endTime: string | null | undefined },
		other: PermitIntervals | undefined,
	) =>
		!!comparison &&
		timeRangeText(time.startTime, time.endTime) !==
			(other && timeRangeText(other.startDateTimeISO, other.endDateTimeISO, otherTimezone));

	const startDateClassName = (changed: boolean) =>
		classNames(changedValueClassName(side, changed), "min-w-[13ch] tabular-nums");

	const { isCollapsible, hiddenCount, isHidden } = getIntervalCollapse({ count: intervals.length, collapse });
	const isCollapsed = isCollapsible && !showAllIntervals;
	const noIntervals = (permitIntervals ?? []).length === 0;

	return (
		<div className={classNames(isCollapsed ? "print:space-y-4" : "space-y-4")}>
			{noIntervals ? (
				<RulesPreview timezone={timezone} isInstant={isInstant} context={context} comparison={comparison} />
			) : (
				intervals.map((interval, index) => {
					const hasSchedule = interval.schedules && interval.schedules.length > 0;
					const prevInterval = index > 0 ? intervals[index - 1] : null;
					const followsHiddenRow = isCollapsed && isHidden(index - 1);
					const showDivider = prevInterval && prevInterval.type !== interval.type;
					const showViewMore = isCollapsed && index === collapse.headCount - 1;
					const other = otherIntervals[interval.index];

					return (
						<div
							className={classNames(
								"relative",
								isCollapsed && isHidden(index) ? "hidden print:block" : undefined,
								isCollapsed && index > 0 && index < collapse.headCount ? "mt-4" : undefined,
							)}
							key={interval.startDateTimeISO}
						>
							{showDivider && (
								<div
									className={classNames(
										"my-4 border-neutral-faded border-t",
										followsHiddenRow ? "hidden print:block" : undefined,
									)}
								/>
							)}
							<AccessibleContent
								label={announcedInterval({ interval, timezone, isInstant, parkingOption, locale })}
							>
								{interval.type === PERMIT_INTERVAL_TYPE.SINGLE ? (
									<div className="flex items-center gap-4">
										<div className="flex items-center gap-2">
											<DateIcon className="size-4" />
											<Text
												size="sm"
												weight="bold"
												intent={textIntent}
												className={startDateClassName(
													isChanged(
														dateText,
														interval.startDateTimeISO,
														other?.startDateTimeISO,
													),
												)}
											>
												{dateText(interval.startDateTimeISO)}
											</Text>
										</div>
										<div className="flex flex-wrap gap-x-4 gap-y-2">
											{interval.times.map((x) => (
												<TimeBadge
													key={x.startTime.toString()}
													side={side}
													changed={isTimeRangeChanged(x, otherIntervals[x.index])}
												>
													{timeRangeText(x.startTime, x.endTime)}
												</TimeBadge>
											))}
										</div>
									</div>
								) : (
									<div className="space-y-2">
										<div className="flex items-start gap-3">
											<DateRangeIcon className="mt-[1px] size-4" />
											<div className="flex items-start gap-4">
												{isInstant ? (
													<div className="flex items-start">
														<Text size="sm" weight="bold" intent={textIntent}>
															{parkingOption.startsNow}
														</Text>
													</div>
												) : (
													<div className="flex flex-col items-start">
														<Text
															size="sm"
															weight="bold"
															intent={textIntent}
															className={startDateClassName(
																isChanged(
																	dateText,
																	interval.startDateTimeISO,
																	other?.startDateTimeISO,
																),
															)}
														>
															{dateText(interval.startDateTimeISO)}
														</Text>
														{!hasSchedule && (
															<TimeBadge
																side={side}
																changed={isChanged(
																	timeText,
																	interval.startDateTimeISO,
																	other?.startDateTimeISO,
																)}
															>
																{timeText(interval.startDateTimeISO)}
															</TimeBadge>
														)}
													</div>
												)}
												<ArrowRightIcon className="mt-[2px] size-4 shrink-0" />
												<div className="flex flex-col items-start">
													<Text
														size="sm"
														weight="bold"
														intent={textIntent}
														className={changedValueClassName(
															side,
															isChanged(
																dateText,
																interval.endDateTimeISO,
																other?.endDateTimeISO,
															),
														)}
													>
														{interval.endDateTimeISO
															? dateText(interval.endDateTimeISO)
															: parkingOption.noExpiry}
													</Text>
													{!hasSchedule && interval.endDateTimeISO && (
														<TimeBadge
															side={side}
															changed={isChanged(
																timeText,
																interval.endDateTimeISO,
																other?.endDateTimeISO,
															)}
														>
															{timeText(interval.endDateTimeISO)}
														</TimeBadge>
													)}
												</div>
											</div>
										</div>
									</div>
								)}
							</AccessibleContent>
							{interval.type === PERMIT_INTERVAL_TYPE.DATE_RANGE &&
								interval.schedules &&
								interval.schedules.length > 0 && (
									<div className="mt-2 ml-6">
										<ParkingTimeSchedule
											interval={{
												type: interval.type,
												startDateTimeISO: interval.startDateTimeISO,
												endDateTimeISO: interval.endDateTimeISO,
												schedules: interval.schedules,
											}}
											timezone={timezone}
											comparison={
												comparison && {
													side: comparison.side,
													other: other && {
														startDateTimeISO: other.startDateTimeISO,
														endDateTimeISO: other.endDateTimeISO,
														schedules: other.schedules ?? [],
														timezone: otherTimezone,
													},
												}
											}
										/>
									</div>
								)}
							{showViewMore && (
								<div className="relative py-2 print:hidden">
									<div className="absolute inset-y-0 left-[7px] border border-neutral-strong border-l border-dashed" />
									<Button
										type="button"
										onClick={(e) => {
											e.preventDefault();
											setShowAllIntervals(true);
										}}
										intent="tertiary"
										className="ml-4"
									>
										{parkingOption.viewMore(hiddenCount)}
									</Button>
								</div>
							)}
						</div>
					);
				})
			)}
		</div>
	);
};

const announcedInterval = ({
	interval,
	timezone,
	isInstant,
	parkingOption,
	locale,
}: {
	interval: {
		type: PermitIntervalType;
		startDateTimeISO: string;
		endDateTimeISO: string | undefined;
		times: { startTime: string; endTime: string | null | undefined }[];
		hasSchedule: boolean;
	};
	timezone: string;
	isInstant: boolean;
	parkingOption: ReturnType<typeof useDesignSystemI18n>["parkingOption"];
	locale: string;
}) => {
	if (interval.type === PERMIT_INTERVAL_TYPE.SINGLE) {
		return [
			formatDate({ date: interval.startDateTimeISO, formatType: "Long_Date", timezone, language: locale }),
			...interval.times.map((x) =>
				formatDateRange({
					startDate: x.startTime,
					endDate: x.endTime ?? undefined,
					formatType: "Abbreviated_Time",
					timezone,
					language: locale,
				}),
			),
		].join(", ");
	}
	const start = isInstant
		? parkingOption.startsNow
		: announcedDate({
				dateTimeISO: interval.startDateTimeISO,
				timezone,
				hasSchedule: interval.hasSchedule,
				locale,
			});
	if (!interval.endDateTimeISO) {
		return `${start} – ${parkingOption.noExpiry}`;
	}
	return `${start} – ${announcedDate({ dateTimeISO: interval.endDateTimeISO, timezone, hasSchedule: interval.hasSchedule, locale })}`;
};

const announcedDate = ({
	dateTimeISO,
	timezone,
	hasSchedule,
	locale,
}: {
	dateTimeISO: string;
	timezone: string;
	hasSchedule: boolean;
	locale: string;
}) => {
	const date = formatDate({ date: dateTimeISO, formatType: "Long_Date", timezone, language: locale });
	if (hasSchedule) {
		return date;
	}
	return `${date}, ${formatDate({ date: dateTimeISO, formatType: "Abbreviated_Time", timezone, language: locale })}`;
};
